import { documentToHtmlString } from '@contentful/rich-text-html-renderer'
import { FlexContainer } from '@news12/storybook-news'

import React, { useContext, useEffect, useState } from 'react'

import internalApiQueryWrapper from '../../core/internalApiQueryWrapper'
import useLocalWeatherEnabled from '../../hooks/useLocalWeatherEnabled'
import useMediaQuery from '../../hooks/useMediaQuery'
import { SubdomainContext } from '../../pages/_app.page'
import style from '../../styles/Weather/weather_index'
import BaseCSS from '../../styles/global/BaseCSS'
import AdUnit from '../Ads/AdUnit'
import Collection from '../Collection/Collection'
import Loading from '../Loading'
import UnresetCSS from '../UnresetCSS'
import VideoPlayer from '../utilities/VideoPlayer'
import CurrentConditions from './CurrentConditions'
import HourlyConditions from './HourlyConditions'
import RadarMap from './RadarMap'
import Title from './Title'
import WeatherLocation from './WeatherLocation/WeatherLocation'
import WeeklyConditions from './WeeklyConditions'

import ClientCookies from 'js-cookie'
import { useRouter } from 'next/router'
import PubSub from 'pubsub-js'

export const paramsForWeatherStation = location => ({
  content_type: 'weatherStation',
  'fields.location[near]': `${location.lat},${location.lon}`,
  include: 4,
  limit: 1
})

export const paramsForWeatherConditions = subdomain => ({
  content_type: 'weatherConditions',
  include: 3,
  'sys.id': subdomain
})

const Weather = ({ storyListByCategory, localForecastReport }) => {
  const router = useRouter()
  const isMobileScreen = useMediaQuery(768)
  const { subdomain } = useContext(SubdomainContext)
  const [weatherData, setWeatherData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const isLocalWeatherEnabled = useLocalWeatherEnabled()

  const loadWeather = async (params, locationTitle) => {
    setError(null)
    setLoading(true)
    const data = await internalApiQueryWrapper('/api/contentful/weather', params)

    if (data?.error) {
      setError(data?.error)
      setWeatherData(null)
    }

    const weatherDataWithVideoForecast = {
      ...data,
      videoUrl:
        localForecastReport?.[0]?.fields?.report?.fields?.videoUrls?.['application/x-mpegURL'] ||
        localForecastReport?.[0]?.fields?.report?.fields?.videoUrls?.['video/mp4']
    }

    const weatherData = locationTitle
      ? {
          ...weatherDataWithVideoForecast,
          name: locationTitle
        }
      : weatherDataWithVideoForecast

    PubSub.publish('WeatherDataChanged', weatherData)
    setWeatherData(weatherData)
    setLoading(false)
  }

  useEffect(() => {
    if (isLocalWeatherEnabled) {
      // Load weather data if user pass coordinates by query params
      if (router?.query?.lat && router?.query?.long) {
        loadWeather(paramsForWeatherStation({ lat: router.query?.lat, lon: router.query?.long }))
        return
      }

      // Load weather data if user location is store in cookies
      if (ClientCookies.get('UserWeatherLocation')) {
        const location = JSON.parse(ClientCookies.get('UserWeatherLocation'))
        loadWeather(paramsForWeatherStation(location), location.name)
        return
      }

      // Load weather data by region
      loadWeather(paramsForWeatherConditions(subdomain))
    } else {
      loadWeather(paramsForWeatherConditions(subdomain))
    }
  }, [isLocalWeatherEnabled])

  const locationChanged = location => {
    ClientCookies.set('UserWeatherLocation', JSON.stringify(location))
    loadWeather(paramsForWeatherStation(location), location.name)
  }

  if (error) return <div>Error loading weather</div>

  const beachCamsSection = documentToHtmlString(weatherData?.beachcams) || null

  return (
    <div className='weather'>
      <FlexContainer justifyContent='center' margin={['1.0rem 0 0', '1.0rem auto 0']}>
        <AdUnit adUnitTemplate='leaderboard' id='ad-banner-top' type='leaderboardRedesign' />
      </FlexContainer>
      {isLocalWeatherEnabled && (
        <WeatherLocation locationTitle={weatherData?.name} locationChanged={location => locationChanged(location)} />
      )}

      {!weatherData && loading && (
        <div className='loading-container'>
          <Loading />
        </div>
      )}

      {weatherData && (
        <div style={{ opacity: loading ? 0.5 : 1 }}>
          <div className='top-section'>
            <CurrentConditions forecast={weatherData?.conditions} region={weatherData?.name} />
            <HourlyConditions forecast={weatherData?.hourly} />
            <div className='banner-middle'>
              <AdUnit adUnitTemplate='leaderboard' id='ad-banner-middle-0' type='leaderboardDynamic' />
            </div>
          </div>
          <div className='bottom-section'>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <WeeklyConditions forecast={weatherData?.forecast} />
              <FlexContainer
                className={'banner-sidebar'}
                justifyContent={'center'}
                margin={['1.0rem 0 0', '1.0rem auto 0']}>
                <AdUnit
                  adUnitTemplate='square'
                  id='ad-banner-sidebar'
                  type='sidebar1'
                  uniqueIdentifier='weather-column'
                />
              </FlexContainer>
            </div>
            <div className='bottom-right'>
              <div>
                {weatherData?.videoUrl && (
                  <>
                    <Title title='Local forecast report' />
                    <VideoPlayer src={weatherData?.videoUrl} type={'application/x-mpegURL'} />
                  </>
                )}
              </div>
              <FlexContainer
                className={'banner-middle'}
                justifyContent={'center'}
                margin={['1.0rem 0 0', '1.0rem auto 0']}>
                <AdUnit
                  adUnitTemplate='leaderboard'
                  id='ad-banner-middle-1'
                  type='leaderboardDynamic'
                  uniqueIdentifier='forecast-middle'
                />
              </FlexContainer>
              <RadarMap location={weatherData?.location} />
              {beachCamsSection ? (
                /* c8 ignore next 2 */
                <div className='title-wrapper' dangerouslySetInnerHTML={{ __html: beachCamsSection }} />
              ) : null}
              {storyListByCategory?.length > 0 && (
                <Collection isMobileScreen={isMobileScreen} storyListByCategory={storyListByCategory} withAd={false} />
              )}
            </div>
          </div>
        </div>
      )}
      <FlexContainer className={'banner-sidebar'} justifyContent={'center'}>
        <AdUnit
          adUnitTemplate='square'
          id='ad-banner-sidebar'
          type='sidebar1'
          uniqueIdentifier='weather-middle-bottom'
        />
      </FlexContainer>
      <UnresetCSS />
      <BaseCSS />
      <style jsx>{style}</style>
    </div>
  )
}

export default Weather
